import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ChefHat, Package, BarChart3, LogOut, Menu, X, Store, UtensilsCrossed, Armchair, Pizza, GlassWater, Gift, Award } from 'lucide-react';
import { Restaurant, TenantRole } from '../../lib/types';
import { cn } from '../../lib/utils';
import { isGlobalRole } from '../../lib/tenant';
interface AdminLayoutProps {
  restaurant: Restaurant | null;
  role?: TenantRole;
  onSignOut?: () => void;
  children: React.ReactNode;
}
export function AdminLayout({
  restaurant,
  role = 'admin',
  onSignOut,
  children
}: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  if (!restaurant) return null;
  // Build nav items based on role
  const allNavItems = [{
    name: 'Estaciones',
    path: `/admin/${restaurant.slug}/operacion`,
    icon: Store,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  }, {
    name: 'Dashboard',
    path: `/admin/${restaurant.slug}/dashboard`,
    icon: LayoutDashboard,
    roles: ['owner', 'admin', 'manager'] as TenantRole[]
  }, {
    name: 'Control de Caja',
    path: `/admin/${restaurant.slug}/caja`,
    icon: LayoutDashboard,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  }, {
    name: 'KDS (Todas)',
    path: `/admin/${restaurant.slug}/kds`,
    icon: ChefHat,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  }, {
    name: 'Cocina',
    path: `/admin/${restaurant.slug}/kds?station=cocina`,
    icon: ChefHat,
    roles: ['owner', 'admin', 'manager', 'caja', 'cocina'] as TenantRole[]
  }, {
    name: 'Pizzería',
    path: `/admin/${restaurant.slug}/kds?station=pizzeria`,
    icon: Pizza,
    roles: ['owner', 'admin', 'manager', 'caja', 'pizzeria'] as TenantRole[]
  }, {
    name: 'Batidos',
    path: `/admin/${restaurant.slug}/kds?station=bebidas`,
    icon: GlassWater,
    roles: ['owner', 'admin', 'manager', 'caja', 'bebidas'] as TenantRole[]
  }, {
    name: 'Menú',
    path: `/admin/${restaurant.slug}/menu`,
    icon: UtensilsCrossed,
    roles: ['owner', 'admin', 'manager'] as TenantRole[]
  }, {
    name: 'Mesas',
    path: `/admin/${restaurant.slug}/tables`,
    icon: Armchair,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  }, {
    name: 'Inventario',
    path: `/admin/${restaurant.slug}/inventory`,
    icon: Package,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  }, {
    name: 'Métricas',
    path: `/admin/${restaurant.slug}/metrics`,
    icon: BarChart3,
    roles: ['owner', 'admin', 'manager'] as TenantRole[]
  }, {
    name: 'Lealtad',
    path: `/admin/${restaurant.slug}/loyalty`,
    icon: Award,
    roles: ['owner', 'admin', 'manager'] as TenantRole[]
  }, {
    name: 'Redimir Beneficios',
    path: `/admin/${restaurant.slug}/loyalty/redemption`,
    icon: Gift,
    roles: ['owner', 'admin', 'manager', 'caja'] as TenantRole[]
  }];
  const isCaprichoAdmin = String(restaurant.id) === '2' || restaurant.slug === 'capricho-de-canela';
  const caprichoHidden = ['Estaciones', 'Mesas', 'KDS (Todas)', 'Cocina', 'Pizzería', 'Batidos'];
  // super_admin and platform_admin have full access (same as owner)
  const effectiveRole: TenantRole = role === 'super_admin' || role === 'platform_admin' ? 'owner' : role;
  const navItems = isCaprichoAdmin ? allNavItems.filter(item => !caprichoHidden.includes(item.name) && item.roles.includes(effectiveRole)) : allNavItems.filter(item => item.roles.includes(effectiveRole));
  const handleSignOut = () => {
    if (onSignOut) {
      onSignOut();
    } else {
      navigate(`/admin/${restaurant.slug}/login`);
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row font-sans" data-id="element-973">
      {/* Mobile Header */}
      <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-50 shadow-sm" data-id="element-974">
        <div className="flex items-center gap-3" data-id="element-975">
          {restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-8 h-8 rounded-lg object-cover border border-gray-100" data-id="element-976" /> : <div className="w-8 h-8 bg-brand-charcoal rounded-lg flex items-center justify-center" data-id="element-977">
              <Store className="w-4 h-4 text-white" data-id="element-978" />
            </div>}
          <span className="font-bold text-gray-900 truncate max-w-[150px]" data-id="element-979">
            {restaurant.name}
          </span>
        </div>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 -mr-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-980">
          {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-981" /> : <Menu className="w-6 h-6" data-id="element-982" />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={cn('fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 flex flex-col shadow-xl md:shadow-none', isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full')} data-id="element-983">
        {/* Sidebar Header */}
        <div className="p-6 border-b border-gray-100 flex items-center gap-3" data-id="element-984">
          {restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-10 h-10 rounded-xl object-cover shadow-sm border border-gray-100" data-id="element-985" /> : <div className="w-10 h-10 bg-brand-charcoal rounded-xl flex items-center justify-center shadow-sm" data-id="element-986">
              <Store className="w-5 h-5 text-white" data-id="element-987" />
            </div>}
          <div className="flex-1 min-w-0" data-id="element-988">
            <h2 className="font-bold text-gray-900 truncate text-lg" data-id="element-989">
              {restaurant.name}
            </h2>
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider" data-id="element-990">
              {role}
            </p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1.5" data-id="element-991">
          {navItems.map(item => {
          // Support query params in active state detection
          const itemUrl = new URL(item.path, window.location.origin);
          const currentUrl = new URL(location.pathname + location.search, window.location.origin);
          const isActive = item.path.includes('?') ? itemUrl.pathname === currentUrl.pathname && itemUrl.search === currentUrl.search : location.pathname === item.path && !location.search;
          return <Link key={item.path} to={item.path} onClick={() => setIsMobileMenuOpen(false)} className={cn('flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-200', isActive ? 'bg-brand-charcoal text-white shadow-md shadow-gray-900/10' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900')} data-id="element-992">
                <item.icon className={cn('w-5 h-5', isActive ? 'text-white' : 'text-gray-400')} data-id="element-993" />
                {item.name}
              </Link>;
        })}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-100" data-id="element-994">
          <button onClick={handleSignOut} className="flex items-center gap-3 px-4 py-3 w-full rounded-xl font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors" data-id="element-995">
            <LogOut className="w-5 h-5 text-gray-400 group-hover:text-red-500" data-id="element-996" />
            Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-30 md:hidden transition-opacity" onClick={() => setIsMobileMenuOpen(false)} data-id="element-997" />}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-[#FAFAFA]" data-id="element-998">
        <div className="flex-1 overflow-y-auto" data-id="element-999">{children}</div>
      </main>
    </div>;
}